import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronRight, Loader2, Minus, Package, Plus, QrCode, ShieldCheck, ShoppingBag, X,
} from "lucide-react";
import { http, fmtErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { useConfig } from "../contexts/ConfigContext";
import { ProductImage } from "../components/ProductImage";
import { QuickAddCard } from "../components/QuickAddCard";

export default function Portal() {
  const { user } = useAuth();
  const { items, setQty, remove, clear } = useCart();
  const { purchases_enabled } = useConfig();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [checkingOut, setCheckingOut] = useState(false);
  const nav = useNavigate();

  useEffect(() => { http.get("/products").then(({ data }) => setProducts(data.products)); }, []);
  useEffect(() => {
    if (user === false) nav("/login");
  }, [user, nav]);
  useEffect(() => {
    if (user && user.id && user.role !== "admin") {
      http.get("/orders/mine").then(({ data }) => setOrders(data.orders)).catch(() => {});
    }
  }, [user]);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const cartLines = items.map((i) => ({ ...i, product: productMap[i.product_id] })).filter((l) => l.product);
  const total = cartLines.reduce((s, l) => s + l.product.case_price * l.quantity, 0);
  const isApproved = user && (user.is_approved || user.role === "admin");

  const checkout = async () => {
    setCheckingOut(true);
    try {
      const { data } = await http.post("/checkout/session", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
      setCheckingOut(false);
    }
  };

  if (user === null) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (!user) return null;

  return (
    <div className="bg-[#FFFDF8]" data-testid="portal-root">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 pb-8 border-b border-[#E5D8C1]">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#D32F2F]">Buyer Portal</div>
            <h1 className="font-heading text-5xl md:text-6xl uppercase tracking-wide mt-2">Hi, {user.contact_name || user.email}</h1>
            <div className="text-[#5C5A56] mt-2">{user.business_name}</div>
          </div>
          <div className="text-right">
            <div className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest ${isApproved ? "bg-[#7CB342] text-white" : "bg-[#FFB300] text-[#1A1A1A]"}`} data-testid="portal-status">
              <ShieldCheck size={14} /> {isApproved ? "Approved wholesaler" : "Pending approval"}
            </div>
          </div>
        </div>

        {!isApproved && (
          <div className="mt-8 bg-[#FFB300]/20 border border-[#FFB300] p-6">
            <div className="font-heading text-xl uppercase tracking-wide">Approval pending</div>
            <p className="text-[#5C5A56] mt-2">Our team reviews new accounts within 1-2 business days. You can browse the catalog and prepare a cart — checkout unlocks once approved.</p>
          </div>
        )}

        {isApproved && (
          <Link to="/invite" className="mt-8 bg-[#1A1A1A] text-[#FFFDF8] p-6 flex items-center justify-between gap-4 group hover:bg-[#D32F2F] transition-colors" data-testid="portal-invite-cta">
            <div className="flex items-center gap-4">
              <QrCode size={32} className="text-[#FFB300] group-hover:text-white shrink-0" />
              <div>
                <div className="font-heading text-xl uppercase tracking-wide">Your wholesale invite link</div>
                <div className="text-sm text-white/70">Share your unique QR code with other shop owners. Print it for your booth.</div>
              </div>
            </div>
            <ChevronRight size={22} className="shrink-0" />
          </Link>
        )}

        <div className="grid md:grid-cols-3 gap-10 mt-12">
          <div className="md:col-span-2">
            <h2 className="font-heading text-3xl uppercase tracking-wide mb-6">Your cart</h2>
            {cartLines.length === 0 ? (
              <div className="border border-dashed border-[#E5D8C1] p-12 text-center text-[#5C5A56]" data-testid="portal-cart-empty">
                <ShoppingBag size={40} className="mx-auto mb-3 opacity-40" />
                Your cart is empty. <Link to="/catalog" className="text-[#D32F2F] font-semibold">Browse the catalog →</Link>
              </div>
            ) : (
              <div className="space-y-4">
                {cartLines.map((l) => (
                  <div key={l.product_id} className="bg-[#FDF4E3] border border-[#E5D8C1] p-4 flex items-center gap-4" data-testid={`cart-line-${l.product_id}`}>
                    <div className="w-20 h-20 shrink-0 relative overflow-hidden">
                      <ProductImage p={l.product} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading text-lg uppercase leading-tight">{l.product.name}</div>
                      <div className="text-sm text-[#5C5A56]">${l.product.case_price.toFixed(2)} / case · {l.product.case_units} × {l.product.size_oz}oz</div>
                    </div>
                    <div className="flex items-center border-2 border-[#1A1A1A]">
                      <button onClick={() => setQty(l.product_id, l.quantity - 1)} className="w-9 h-9 flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white" data-testid={`cart-minus-${l.product_id}`}><Minus size={14} /></button>
                      <div className="w-10 text-center text-sm font-semibold">{l.quantity}</div>
                      <button onClick={() => setQty(l.product_id, l.quantity + 1)} className="w-9 h-9 flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white" data-testid={`cart-plus-${l.product_id}`}><Plus size={14} /></button>
                    </div>
                    <div className="w-24 text-right font-heading text-xl">${(l.product.case_price * l.quantity).toFixed(2)}</div>
                    <button onClick={() => remove(l.product_id)} className="text-[#5C5A56] hover:text-[#D32F2F]" data-testid={`cart-remove-${l.product_id}`}><X size={18} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="bg-[#1A1A1A] text-[#FFFDF8] p-8 sticky top-24" data-testid="portal-summary">
              <h3 className="font-heading text-2xl uppercase tracking-wide">Order summary</h3>
              <div className="flex justify-between mt-6 pb-4 border-b border-white/20">
                <span className="text-sm uppercase tracking-widest text-white/70">Cases</span>
                <span>{cartLines.reduce((s, l) => s + l.quantity, 0)}</span>
              </div>
              <div className="flex justify-between mt-4">
                <span className="text-sm uppercase tracking-widest text-white/70">Subtotal</span>
                <span className="font-heading text-3xl text-[#FFB300]">${total.toFixed(2)}</span>
              </div>
              <div className="text-xs text-white/50 mt-2">Shipping & tax invoiced separately.</div>
              <button
                disabled={!isApproved || cartLines.length === 0 || checkingOut || !purchases_enabled}
                onClick={checkout}
                className="mt-6 w-full bg-[#D32F2F] hover:bg-[#B71C1C] py-4 uppercase tracking-widest font-heading text-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="portal-checkout"
              >
                {checkingOut && <Loader2 className="animate-spin" size={18} />}
                {!purchases_enabled ? "Orders launching soon" : isApproved ? "Checkout with Stripe" : "Awaiting approval"}
              </button>
              {cartLines.length > 0 && (
                <button onClick={clear} className="mt-3 w-full text-xs uppercase tracking-widest text-white/50 hover:text-white" data-testid="portal-clear">Clear cart</button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-16" data-testid="portal-orders">
          <h2 className="font-heading text-3xl uppercase tracking-wide mb-6 flex items-center gap-3">
            <Package size={22} className="text-[#D32F2F]" /> Order history
          </h2>
          {orders.length === 0 ? (
            <div className="border border-dashed border-[#E5D8C1] p-8 text-center text-[#5C5A56]" data-testid="portal-orders-empty">
              No orders yet. Once you place a wholesale order it&apos;ll show up here with tracking + status.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-[#1A1A1A] text-left text-xs uppercase tracking-widest">
                    <th className="py-3 pr-4">When</th><th className="pr-4">Amount</th><th className="pr-4">Cases</th><th className="pr-4">Status</th><th>Tracking</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-b border-[#E5D8C1]" data-testid={`portal-order-${o.id}`}>
                      <td className="py-3 pr-4 text-[#5C5A56] whitespace-nowrap">{o.created_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="pr-4 font-heading">${(o.amount || 0).toFixed(2)}</td>
                      <td className="pr-4">{(o.breakdown || []).reduce((s, l) => s + (l.quantity || 0), 0)}</td>
                      <td className="pr-4">
                        <div className="flex flex-col gap-1">
                          <span className={`text-[10px] uppercase font-heading px-2 py-1 w-fit ${o.payment_status === "paid" ? "bg-[#7CB342] text-white" : o.payment_status === "initiated" ? "bg-[#FFB300] text-[#1A1A1A]" : "bg-[#5C5A56] text-white"}`}>{o.payment_status}</span>
                          {o.fulfillment_status && (
                            <span className={`text-[10px] uppercase font-heading px-2 py-1 w-fit ${o.fulfillment_status === "delivered" ? "bg-[#1A1A1A] text-white" : o.fulfillment_status === "shipped" ? "bg-[#D32F2F] text-white" : "bg-[#E5D8C1] text-[#1A1A1A]"}`}>{o.fulfillment_status}</span>
                          )}
                        </div>
                      </td>
                      <td className="text-xs">
                        {o.tracking_number ? (
                          <div>
                            <div className="text-[10px] uppercase text-[#5C5A56]">{(o.carrier || "").toUpperCase()}</div>
                            <div className="font-mono">{o.tracking_number}</div>
                            {o.tracking_url && <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-[10px] text-[#D32F2F] uppercase tracking-widest">Track →</a>}
                          </div>
                        ) : (<span className="text-[#5C5A56]">—</span>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-16">
          <h2 className="font-heading text-3xl uppercase tracking-wide mb-6">Quick add</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {products.map((p) => <QuickAddCard key={p.id} p={p} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

export function CheckoutSuccess() {
  const [status, setStatus] = useState("checking");
  const [details, setDetails] = useState(null);
  const { clear } = useCart();
  const location = useLocation();
  const sessionId = new URLSearchParams(location.search).get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await http.get(`/checkout/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === "paid") { setStatus("paid"); clear(); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
      } catch (e) { /* keep polling */ }
      attempts++;
      if (attempts < 6) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="success-root">
      {status === "checking" && (<>
        <Loader2 className="animate-spin mx-auto mb-4" size={40} />
        <h1 className="font-heading text-4xl uppercase tracking-wide">Confirming your payment…</h1>
        <p className="text-[#5C5A56] mt-3">Hang tight, this only takes a moment.</p>
      </>)}
      {status === "paid" && (<>
        <div className="w-20 h-20 mx-auto bg-[#7CB342] flex items-center justify-center mb-6"><ShieldCheck className="text-white" size={40} /></div>
        <h1 className="font-heading text-5xl uppercase tracking-wide">Order confirmed</h1>
        <p className="text-[#5C5A56] mt-3">
          Thank you! We received your payment{details?.amount_total ? ` of $${(details.amount_total / 100).toFixed(2)}` : ""}. Your cases will ship within 2 business days — we&apos;ll email a tracking number.
        </p>
        <Link to="/portal" className="inline-block mt-8 border-2 border-[#1A1A1A] px-8 py-3 uppercase tracking-widest font-heading hover:bg-[#1A1A1A] hover:text-white" data-testid="success-back-portal">Back to portal</Link>
      </>)}
      {(status === "expired" || status === "error" || status === "timeout") && (<>
        <h1 className="font-heading text-4xl uppercase tracking-wide">Payment {status}</h1>
        <p className="text-[#5C5A56] mt-3">If you were charged and don&apos;t see confirmation, contact us with session id <code className="bg-[#FDF4E3] px-2 py-1">{sessionId || "unknown"}</code>.</p>
        <Link to="/portal" className="inline-block mt-8 border-2 border-[#1A1A1A] px-8 py-3 uppercase tracking-widest font-heading hover:bg-[#1A1A1A] hover:text-white">Back to portal</Link>
      </>)}
    </div>
  );
}
